import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Loader2, CheckCircle2, Copy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function AdminMFASetup() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [isAlreadyEnrolled, setIsAlreadyEnrolled] = useState(false);

  useEffect(() => {
    checkMFAStatus();
  }, []);

  const checkMFAStatus = async () => {
    try {
      const { data: factors, error } = await supabase.auth.mfa.listFactors();
      
      if (error) throw error;

      const totpFactor = factors?.totp?.find(f => f.status === 'verified');
      if (totpFactor) {
        setIsAlreadyEnrolled(true);
      }
    } catch (error) {
      console.error("Error checking MFA status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const startEnrollment = async () => {
    setIsEnrolling(true);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authentificateur Admin'
      });

      if (error) throw error;

      if (data) {
        setQrCode(data.totp.qr_code);
        setSecret(data.totp.secret);
        setFactorId(data.id);
      }
    } catch (error: any) {
      console.error("Error enrolling MFA:", error);
      toast.error(error.message || "Erreur lors de l'activation du 2FA");
    } finally {
      setIsEnrolling(false);
    }
  };

  const verifyAndComplete = async () => {
    if (!factorId || verificationCode.length !== 6) {
      toast.error("Veuillez entrer un code à 6 chiffres");
      return;
    }

    setIsVerifying(true);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verificationCode,
      });

      if (verifyError) throw verifyError;

      toast.success("2FA activé avec succès ! Votre compte admin est maintenant sécurisé.");
      navigate("/admin");
    } catch (error: any) {
      console.error("Error verifying MFA:", error);
      toast.error(error.message || "Code invalide. Veuillez réessayer.");
    } finally {
      setIsVerifying(false);
    }
  };

  const copySecret = () => {
    if (secret) {
      navigator.clipboard.writeText(secret);
      toast.success("Code secret copié !");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isAlreadyEnrolled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <CardTitle>2FA Déjà Activé</CardTitle>
            <CardDescription>
              L'authentification à deux facteurs est déjà configurée sur votre compte.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full" 
              onClick={() => navigate("/admin")}
            >
              Accéder au tableau de bord
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-3 bg-primary/10 rounded-full w-fit">
            <Shield className="h-10 w-10 text-primary" />
          </div>
          <CardTitle className="text-xl">Configuration 2FA Obligatoire</CardTitle>
          <CardDescription>
            Pour protéger votre compte administrateur, vous devez activer l'authentification à deux facteurs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!qrCode ? (
            <>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <div className="flex gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-500 mb-1">Sécurité Obligatoire</p>
                    <p className="text-muted-foreground">
                      Vous aurez besoin d'une application d'authentification comme Google Authenticator, Authy ou 1Password.
                    </p>
                  </div>
                </div>
              </div>

              <Button 
                className="w-full" 
                onClick={startEnrollment}
                disabled={isEnrolling}
              >
                {isEnrolling ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Génération...
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4 mr-2" />
                    Configurer le 2FA
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Scannez ce QR code avec votre application d'authentification
                </p>
                <div className="bg-white p-4 rounded-lg inline-block">
                  <img src={qrCode} alt="QR Code 2FA" className="w-48 h-48" />
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs text-muted-foreground text-center">
                  Ou entrez ce code manuellement :
                </p>
                <div className="flex items-center gap-2 bg-muted p-2 rounded-lg">
                  <code className="text-xs flex-1 font-mono break-all">{secret}</code>
                  <Button variant="ghost" size="icon" onClick={copySecret}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Code de vérification</label>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-widest font-mono"
                />
                <p className="text-xs text-muted-foreground text-center">
                  Entrez le code à 6 chiffres affiché dans votre application
                </p>
              </div>

              <Button 
                className="w-full" 
                onClick={verifyAndComplete}
                disabled={isVerifying || verificationCode.length !== 6}
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Vérification...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Activer le 2FA
                  </>
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
